import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { mockNotes } from '@/data/mockData';
import { FileText, Plus, Trash2, Pencil, Reply, ChevronDown, ChevronRight, User } from 'lucide-react';
import { logActivity } from '@/hooks/useActivityLog';
import { useAuth } from '@/contexts/AuthContext';
import { getEffectiveRole } from '@/constants/roles';
import { MentionTextarea } from '@/components/MentionTextarea';
import { useMentions } from '@/hooks/useMentions';
import { formatNoteText } from '@/utils/formatNoteText';


interface Note {
  id: string;
  title: string;
  content: string;
  created_at: string;
  created_by: string;
  author_name?: string;
  site_id?: string;
  category?: string | null;
  parent_note_id?: string | null;
}

const parseStructuredNoteContent = (content?: string | null) => {
  if (!content) return null;
  try {
    return JSON.parse(content);
  } catch {
    return null;
  }
};

const formatNoteContent = (note?: Pick<Note, 'content' | 'title' | 'category'> | null) => {
  const rawContent = note?.content || '';
  const parsed = parseStructuredNoteContent(rawContent);

  if ((note?.category === 'property_return' || note?.title === 'Property Returned') && parsed) {
    const lines: string[] = [];

    if (parsed.allReturned) {
      lines.push('All items returned');
    }
    if (Array.isArray(parsed.returnedItems) && parsed.returnedItems.length > 0) {
      lines.push(`Returned: ${parsed.returnedItems.join('; ')}`);
    }
    if (Array.isArray(parsed.notReturned) && parsed.notReturned.length > 0) {
      lines.push(`Not returned: ${parsed.notReturned.join(', ')}`);
    }
    if (parsed.missingOrDamaged && parsed.missingOrDamaged !== 'None') {
      lines.push(`Missing/Damaged: ${parsed.missingOrDamaged}`);
    }

    return lines.join('\n') || 'Property return recorded';
  }

  if ((note?.category === 'property' || note?.title === 'Property Stored' || note?.title === 'Unbedded Belongings') && parsed) {
    const lines = [
      parsed.propertyLocation ? `Location: ${parsed.propertyLocation}` : null,
      parsed.electronics ? `Electronics: ${parsed.electronics}` : null,
      parsed.documents ? `Documents: ${parsed.documents}` : null,
      parsed.valuables ? `Valuables: ${parsed.valuables}` : null,
      parsed.additionalNotes ? `Notes: ${parsed.additionalNotes}` : null,
    ].filter(Boolean);

    return lines.join('\n') || 'Property record saved';
  }

  return formatNoteText(rawContent) || 'No content';
};

export function NotesSection({ isDemo = false }: { isDemo?: boolean }) {
  const { userSites, userRole } = useAuth();
  const { createMentionsAsync } = useMentions();
  const [notes, setNotes] = useState<Note[]>([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [viewingNote, setViewingNote] = useState<Note | null>(null);
  const [editingNote, setEditingNote] = useState<Note | null>(null);
  const [title, setTitle] = useState('');
  const [noteSearch, setNoteSearch] = useState('');
  const [content, setContent] = useState('');

  const [loading, setLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [expandedThreads, setExpandedThreads] = useState<Set<string>>(new Set());
  
  const siteId = userSites?.[0];
  const effectiveRole = getEffectiveRole(userRole);
  const canEditDelete = effectiveRole === 'SUPER_ADMIN' || effectiveRole === 'MANAGER';

  const loadNotes = async () => {
    if (isDemo) {
      setNotes(mockNotes);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .or('category.is.null,category.neq.shift-report')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const authorIds = [...new Set((data || []).map(note => note.created_by).filter(Boolean))];
      let authorMap = new Map<string, string>();

      if (authorIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', authorIds);

        authorMap = new Map((profiles || []).map(profile => [profile.id, profile.full_name || 'Unknown']));
      }

      setNotes((data || []).map(note => ({
        ...note,
        category: note.category ?? null,
        author_name: authorMap.get(note.created_by) || 'Unknown'
      })));
    } catch (error: any) {
      console.error('Error loading notes:', error);
    }
  };

  useEffect(() => {
    loadNotes();
  }, [isDemo]);

  const handleCreate = async () => {
    if (!title.trim()) return;

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('You must be signed in to create notes');
      
      const { data: note, error } = await supabase
        .from('notes')
        .insert([{
          title,
          content,
          created_by: user?.id,
          site_id: siteId || null
        }])
        .select('id')
        .single();

      if (error) throw error;

      if (content.includes('@[')) {
        await createMentionsAsync({ text: content, noteId: note.id });
      }

      await logActivity({
        actionType: 'create_note',
        actionDetails: {
          note_title: title,
          note_source: 'manager_notes',
          timestamp: new Date().toISOString()
        },
        siteId: siteId || undefined
      });

      toast({ title: 'Success', description: 'Note created successfully' });
      setTitle('');
      setContent('');
      setIsCreateDialogOpen(false);
      loadNotes();
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (parentNoteId: string) => {
    if (!replyContent.trim()) return;

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('You must be signed in to reply');
      const parentNote = notes.find(n => n.id === parentNoteId);

      const { data: reply, error } = await supabase
        .from('notes')
        .insert([{
          title: `Re: ${parentNote?.title || 'Note'}`,
          content: replyContent,
          created_by: user?.id,
          site_id: siteId || null,
          parent_note_id: parentNoteId
        }])
        .select('id')
        .single();

      if (error) throw error;

      if (replyContent.includes('@[')) {
        await createMentionsAsync({ text: replyContent, noteId: reply.id });
      }

      await logActivity({
        actionType: 'create_note',
        actionDetails: {
          note_title: `Re: ${parentNote?.title || 'Note'}`,
          note_source: 'manager_notes_reply',
          parent_note_id: parentNoteId,
          timestamp: new Date().toISOString()
        },
        siteId: siteId || undefined
      });

      toast({ title: 'Success', description: 'Reply added' });
      setReplyContent('');
      setReplyingTo(null);
      setExpandedThreads(prev => new Set(prev).add(parentNoteId));
      loadNotes();
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    } finally {
      setLoading(false);
    }
  };

  const toggleThread = (noteId: string) => {
    setExpandedThreads(prev => {
      const next = new Set(prev);
      if (next.has(noteId)) next.delete(noteId);
      else next.add(noteId);
      return next;
    });
  };

  const handleEdit = (note: Note) => {
    setEditingNote(note);
    setTitle(note.title);
    setContent(note.content || '');
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingNote || !title.trim()) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('notes')
        .update({ title, content })
        .eq('id', editingNote.id);

      if (error) throw error;

      toast({ title: 'Success', description: 'Note updated successfully' });
      setTitle('');
      setContent('');
      setEditingNote(null);
      setIsEditDialogOpen(false);
      loadNotes();
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (noteId: string) => {
    if (!confirm('Delete this note?')) return;

    try {
      const { error } = await supabase
        .from('notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;
      toast({ title: 'Success', description: 'Note deleted' });
      loadNotes();
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    }
  };

  const getReplies = (noteId: string) => notes.filter(n => n.parent_note_id === noteId);

  const searchTerm = noteSearch.trim().toLowerCase();
  const matchesSearch = (note: Note) =>
    !searchTerm ||
    (note.title || '').toLowerCase().includes(searchTerm) ||
    (note.content || '').toLowerCase().includes(searchTerm) ||
    (note.author_name || '').toLowerCase().includes(searchTerm);

  const topLevelNotes = notes
    .filter(n => !n.parent_note_id)
    .filter(n => matchesSearch(n) || getReplies(n.id).some(matchesSearch));


  const renderNoteCard = (note: Note, isReply = false) => {
    const replies = getReplies(note.id);
    const hasReplies = replies.length > 0;
    const isExpanded = expandedThreads.has(note.id);

    return (
      <div key={note.id} className={isReply ? 'ml-6 border-l-2 border-muted pl-3' : ''}>
        <Card 
          className="cursor-pointer transition-colors bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900/50 hover:bg-amber-100/80 dark:hover:bg-amber-950/40"
          onClick={() => {
            setViewingNote(note);
            setIsViewDialogOpen(true);
          }}
        >
          <CardContent className="p-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-sm mb-1">{note.title}</h3>
                {note.content && (
                  <p className="text-xs text-muted-foreground whitespace-pre-wrap line-clamp-2">
                    {formatNoteContent(note)}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1 font-medium text-foreground">
                    <User className="h-3 w-3" />
                    {note.author_name || 'Unknown'}
                  </span>
                  <span>{new Date(note.created_at).toLocaleString()}</span>
                  {!isReply && hasReplies && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-5 px-1.5 text-xs text-muted-foreground"
                      onClick={(e) => { e.stopPropagation(); toggleThread(note.id); }}
                    >
                      {isExpanded ? <ChevronDown className="h-3 w-3 mr-0.5" /> : <ChevronRight className="h-3 w-3 mr-0.5" />}
                      {replies.length} {replies.length === 1 ? 'reply' : 'replies'}
                    </Button>
                  )}
                </div>
              </div>
              <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                {!isReply && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setReplyingTo(replyingTo === note.id ? null : note.id)}
                    className="h-7 w-7 p-0"
                    title="Reply"
                  >
                    <Reply className="h-3 w-3" />
                  </Button>
                )}
                {canEditDelete && (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(note)} className="h-7 w-7 p-0">
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(note.id)} className="h-7 w-7 p-0">
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Reply form */}
        {replyingTo === note.id && (
          <div className="ml-6 mt-1 space-y-2 p-3 border rounded-md bg-muted/20">
            <MentionTextarea
              value={replyContent}
              onChange={setReplyContent}
              placeholder="Write a reply..."
              className="min-h-16"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={() => handleReply(note.id)} disabled={loading || !replyContent.trim()}>
                <Reply className="h-3 w-3 mr-1" /> Reply
              </Button>
              <Button size="sm" variant="outline" onClick={() => { setReplyingTo(null); setReplyContent(''); }}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Threaded replies */}
        {!isReply && isExpanded && replies
          .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
          .map(reply => renderNoteCard(reply, true))
        }
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Notes</h2>
          <p className="text-xs text-muted-foreground">Manage documentation and notes</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-3 w-3 mr-1.5" />
              Add Note
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Note</DialogTitle>
              <DialogDescription>Add documentation or important information</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label htmlFor="note-title" className="text-xs">Title *</Label>
                <Input
                  id="note-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Note title"
                  className="h-8 text-sm"
                />
              </div>
              <div>
                <Label htmlFor="note-content" className="text-xs">Content</Label>
                <MentionTextarea
                  value={content}
                  onChange={setContent}
                  placeholder="Note content... Use @mentions to tag staff"
                  className="min-h-32 text-sm"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleCreate} disabled={loading || !title.trim()}>
                  Create Note
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Input
        value={noteSearch}
        onChange={(e) => setNoteSearch(e.target.value)}
        placeholder="Search notes by title, text or author"
        className="h-9 text-sm"
      />


      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
        setIsEditDialogOpen(open);
        if (!open) { setEditingNote(null); setTitle(''); setContent(''); }
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Note</DialogTitle>
            <DialogDescription>Update note details</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="edit-note-title" className="text-xs">Title *</Label>
              <Input id="edit-note-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Note title" className="h-8 text-sm" />
            </div>
            <div>
              <Label htmlFor="edit-note-content" className="text-xs">Content</Label>
              <MentionTextarea value={content} onChange={setContent} placeholder="Note content" className="min-h-32 text-sm" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" size="sm" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={handleUpdate} disabled={loading || !title.trim()}>Save Changes</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={(open) => { setIsViewDialogOpen(open); if (!open) setViewingNote(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{viewingNote?.title}</DialogTitle>
            <DialogDescription>
              {viewingNote?.created_at && `${viewingNote.author_name || 'Unknown'} · ${new Date(viewingNote.created_at).toLocaleString()}`}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <p className="text-sm whitespace-pre-wrap">{formatNoteContent(viewingNote)}</p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setIsViewDialogOpen(false)}>Close</Button>
            {canEditDelete && (
              <Button size="sm" onClick={() => { if (viewingNote) { handleEdit(viewingNote); setIsViewDialogOpen(false); } }}>
                <Pencil className="h-3 w-3 mr-1.5" /> Edit
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {topLevelNotes.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-8">
            <FileText className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">{searchTerm ? 'No notes match your search' : 'No notes yet'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {topLevelNotes.map((note) => renderNoteCard(note))}
        </div>
      )}
    </div>
  );
}
